/**
 * Salary slip formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const salarySlipFormatsPack: ToilePack = {
    slug: 'salary-slip-formats',
    category: 'office',
    name: 'Salary slip formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'monthly', label: 'Monthly', subject: 'a salary slip format with earnings and deductions columns and "Net Pay"'},
        {key: 'detailed', label: 'Detailed', subject: 'a salary slip with PF, ESI and TDS lines'},
        {key: 'simple', label: 'Simple', subject: 'a simple payslip format'},
    ],
    styles: [
        {key: 'boxed', label: 'Boxed', style: 'classic boxed pay slip with thin black borders, an earnings column and a deductions column', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'modern', label: 'Modern', style: 'modern pay slip with a coloured title bar, light grey rows and a highlighted net pay cell', palette: 'blue and light grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'shaded', label: 'Shaded', style: 'formal pay slip with shaded header cells, double rule above the total row and a signature box', palette: 'dark green and pale grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['blue header', 'with logo', 'green'],
    directionHint: 'try: blue header',
    seo: {
        title: 'Salary slip format: monthly, detailed, simple',
        metaDescription: 'Nine printable salary slip formats: a monthly, a detailed and a simple pay slip, each in boxed, modern or shaded style.',
        h1: 'Salary slip format: monthly, detailed, simple',
        tagline: 'Three pay slip layouts, each in three styles, with earnings, deductions and net pay marked.',
        intro: 'A salary slip format is the layout of a pay slip, the statement an employee gets each month. These nine show a monthly slip with earnings and deductions; a detailed slip with PF, ESI and TDS lines; and a simple payslip. Only the Net Pay and Earnings headings are lettered. Type a twist, such as blue header, and it is a request.',
        useCases: [
            "a pay slip layout for a small business's staff",
            'a sample pay slip for a class project',
            'a template for an office to fill in each month',
            "a copy for an employee's file",
        ],
        faq: [
            {q: 'What are PF, ESI and TDS?', a: 'PF is provident fund, ESI is employee state insurance, and TDS is tax deducted at source. The detailed slip has a line for each.'},
            {q: 'Does the slip calculate the deductions?', a: 'No. The lines are blank figures. The arithmetic is yours.'},
            {q: 'Can I use it as a real pay slip?', a: 'It is a layout to fill in. Check the rules that apply to your payroll before you issue one.'},
            {q: 'What does the signature box do?', a: 'The shaded style has a signature box at the foot of the page. Sign the printed copy there.'},
        ],
    },
}
